import { FormEventHandler } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Button from "@mui/material/Button";

import { useState, useCallback } from "react";
import { useNotify } from "../../components/@common/NotificationsProvider";
import { useLanguageTranslator } from "../../components";
import I18nSelect from "../../components/@common/I18nSelect";
import { gql, useMutation } from "@apollo/client";
import { toastHandler } from "../../utils/helpers";
import { toastTypesKeys } from "../../utils/constants";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

const forgotPasswordQuery = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(input: { email: $email }) {
      clientMutationId
      message
    }
  }
`;

function ForgotPassword() {
  const notify = useNotify();
  const [email, setEmail] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const navigate = useNavigate();

  const { translator } = useLanguageTranslator();

  const [forgotPassword, { data, loading, error }] =
    useMutation(forgotPasswordQuery);

  /**
   * handleSignin()
   */
  const handleSignin: FormEventHandler<HTMLFormElement> = useCallback(
    async (ev) => {
      try {
        ev.preventDefault();

        setSigningIn(true);

        const response = await forgotPassword({ variables: { email } });

        toastHandler(
          response?.data?.forgotPassword?.message || "",
          toastTypesKeys.success
        );

        if (response?.data?.forgotPassword?.message) {
          setTimeout(() => {
            navigate(`/auth/otp-verification`, {
              state: { email: email },
            });
          }, 2000);
          return;
        }
      } catch (err) {
        notify.error(err as Error);
        setSigningIn(false);
      }
    },
    [email, forgotPassword, notify]
  );

  const isFormEmpty = !email.trim();

  return (
    <Stack sx={{ height: "100%" }} justifyContent="center">
      <ToastContainer />
      <Container maxWidth="xs">
        <Box mb={4}>
          <Typography variant="h3" align="center" gutterBottom>
            {translator("generic.Forgot")} {translator("generic.Password")}
          </Typography>
          <Typography align="center">
            {translator("Please enter your details")}.
          </Typography>
        </Box>

        <Stack component="form" spacing={1.5} onSubmit={handleSignin}>
          <FormControl disabled={signingIn}>
            <FormLabel htmlFor="email">{translator(`generic.Email`)}</FormLabel>
            <OutlinedInput
              id="email"
              name="email"
              type="email"
              placeholder={translator(`generic.Email`)}
              autoFocus
              required
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </FormControl>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isFormEmpty || signingIn}
          >
            {translator("Submit")}
          </Button>
        </Stack>

        <Stack direction="row" alignItems="end" mt={2} justifyContent="center">
          <I18nSelect
            options={[
              { label: translator("English"), value: "en" },
              { label: translator("French"), value: "fr" },
              { label: translator("Portuguese"), value: "pt" },
            ]}
          />
        </Stack>
        <center style={{ marginTop: 32 }}>
          <Link
            href="/"
            tabIndex={-1}
            color="textSecondary"
            variant="caption"
            fontWeight={500}
          >
            {translator("Back to Home")}
          </Link>
        </center>
      </Container>
    </Stack>
  );
}

export { ForgotPassword };
